/**
 * Il manuale utente, caricato a build time dai file Markdown in docs/manuale.
 * Ogni file ha un frontmatter minimale (id, titolo, area, rotte, ordine) e il corpo in Markdown,
 * convertito in HTML una sola volta con marked. Il contenuto è nostro: non serve sanificarlo.
 */
import type { SezioneManuale } from '@aulaviva/ui';
import { marked } from 'marked';

export type AreaManuale = 'tutti' | 'partecipante' | 'relatore' | 'regia' | 'admin';

export interface SezioneIndicizzata extends SezioneManuale {
  area: AreaManuale;
  rotte: string[];
  ordine: number;
}

export interface Frontmatter {
  id: string;
  titolo: string;
  area: AreaManuale;
  rotte: string[];
  ordine: number;
}

const AREE: AreaManuale[] = ['tutti', 'partecipante', 'relatore', 'regia', 'admin'];

/** Legge il blocco `---` iniziale: chiavi scalari e `rotte` come array JSON. Niente dipendenze YAML. */
export function analizzaFrontmatter(sorgente: string): { meta: Frontmatter; corpo: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(sorgente);
  if (!m) throw new Error('frontmatter mancante nel file del manuale');
  const campi: Record<string, string> = {};
  for (const riga of m[1]!.split(/\r?\n/)) {
    const i = riga.indexOf(':');
    if (i < 0) continue;
    campi[riga.slice(0, i).trim()] = riga.slice(i + 1).trim();
  }
  const id = campi.id;
  const titolo = campi.titolo;
  const area = campi.area as AreaManuale;
  if (!id || !titolo || !AREE.includes(area)) throw new Error(`frontmatter incompleto: ${JSON.stringify(campi)}`);
  let rotte: string[] = [];
  if (campi.rotte) {
    const valore: unknown = JSON.parse(campi.rotte);
    if (!Array.isArray(valore) || !valore.every((r) => typeof r === 'string')) throw new Error(`rotte non valide per ${id}`);
    rotte = valore;
  }
  const ordine = Number(campi.ordine ?? 0);
  return { meta: { id, titolo, area, rotte, ordine: Number.isFinite(ordine) ? ordine : 0 }, corpo: sorgente.slice(m[0].length) };
}

/** Markdown → HTML (sincrono). Le tabelle ricevono una classe per lo stile; i link restano com'è. */
export function renderizzaMarkdown(markdown: string): string {
  const html = marked.parse(markdown, { gfm: true, breaks: false, async: false });
  return html.replace(/<table>/g, '<table class="manuale-tabella">');
}

export function costruisciSezione(sorgente: string): SezioneIndicizzata {
  const { meta, corpo } = analizzaFrontmatter(sorgente);
  return { ...meta, html: renderizzaMarkdown(corpo) };
}

const file = import.meta.glob('../../../../docs/manuale/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

/** Tutte le sezioni, in ordine. */
export const SEZIONI: SezioneIndicizzata[] = Object.keys(file)
  .sort()
  .map((k) => costruisciSezione(file[k]!))
  .sort((a, b) => a.ordine - b.ordine);

export const SEZIONE_PREDEFINITA = 'introduzione';
